import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Forgot password — Phase-1 UX
 * Never reveals whether the email exists (per Emergent auth playbook).
 */
export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!clean) return;
    setLoading(true);
    try {
      await api.post("/auth/password-reset/request", { email: clean });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f6f8] grid place-items-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#e6edf1] shadow-sm p-6 sm:p-8" data-testid="forgot-password-page">
        <button
          onClick={() => nav("/login")}
          className="inline-flex items-center gap-1 text-xs text-[#457b9d] hover:text-[#1d3557] mb-6"
          data-testid="fp-back"
        >
          <ArrowLeft size={14} /> Back to login
        </button>

        {!sent ? (
          <>
            <h1 className="text-[22px] font-bold text-[#1d3557] mb-1" data-testid="fp-title">
              Forgot your password?
            </h1>
            <p className="text-sm text-[#667085] mb-6">
              Enter the email you use to log in and we&apos;ll send you a link to
              choose a new password.
            </p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="fp-email" className="text-xs uppercase tracking-wider text-[#457b9d]">Email</Label>
                <div className="mt-1 relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
                  <Input
                    id="fp-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    required
                    data-testid="fp-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full bg-[#e63946] hover:bg-[#d02f3c] text-white"
                data-testid="fp-submit"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : "Send reset link"}
              </Button>
            </form>

            <p className="text-xs text-[#667085] mt-6 leading-relaxed">
              Remember it? <Link to="/login" className="text-[#e63946] font-semibold">Log in instead</Link>.
              Need help? <a href="mailto:contact@fenderapp.com" className="text-[#e63946]">contact@fenderapp.com</a>
            </p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-[#22c55e]/15 text-[#16a34a] grid place-items-center mb-4">
              <CheckCircle2 size={22} />
            </div>
            <h1 className="text-[20px] font-bold text-[#1d3557] mb-2" data-testid="fp-sent">
              Check your inbox
            </h1>
            <p className="text-sm text-[#667085] leading-relaxed">
              If an account exists for <b>{email}</b>, we&apos;ve sent a reset link
              to that address. The link is valid for the next 60 minutes.
            </p>
            <p className="text-xs text-[#98A2B3] mt-3 leading-relaxed">
              Don&apos;t see it? Check your spam folder, or wait a minute and try again.
            </p>
            <Button
              onClick={() => nav("/login")}
              className="w-full mt-6 bg-[#1d3557] hover:bg-[#152841] text-white"
              data-testid="fp-back-to-login"
            >
              Back to login
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
